// @ts-check
/**
 * Support API — families write from the settings sheet; admins answer from the
 * 客服 tab on /admin (threads are per person: key u<user id> or wx<shop openid>).
 *
 * Response Types
 * @typedef {import('./types').MeSupportMessageCreateResponse} MeSupportMessageCreateResponse
 * @typedef {import('./types').MeSupportMessagesResponse} MeSupportMessagesResponse
 * @typedef {import('./types').MeSupportMessagesSeenResponse} MeSupportMessagesSeenResponse
 * @typedef {import('./types').AdminSupportThreadsResponse} AdminSupportThreadsResponse
 * @typedef {import('./types').AdminSupportThreadResponse} AdminSupportThreadResponse
 * @typedef {import('./types').AdminSupportReplyResponse} AdminSupportReplyResponse
 * @typedef {import('./types').AdminSupportThreadStatusResponse} AdminSupportThreadStatusResponse
 * @typedef {import('./types').AdminCustomersLookupResponse} AdminCustomersLookupResponse
 *
 * Request Types
 * @typedef {import('./types').MeSupportMessageRequest} MeSupportMessageRequest
 */

import { apiRequest } from './base';
import { getPageContext } from './pageContext';

/**
 * Send a message to customer support. The page the user is on is attached
 * automatically so Rob knows where they got stuck.
 * @param {{ message: string, contact?: string }} p
 * @returns {Promise<MeSupportMessageCreateResponse>}
 */
export function sendSupportMessage({ message, contact }) {
  const { pagePath } = getPageContext();
  return apiRequest('/me/support-messages', {
    method: 'POST',
    body: JSON.stringify({ message, ...(contact ? { contact } : {}), ...(pagePath ? { pagePath } : {}) }),
  });
}

/**
 * My support threads with Rob's replies, newest first, plus how many replies I haven't seen.
 * @returns {Promise<MeSupportMessagesResponse>}
 */
export function getMySupportMessages() {
  return apiRequest('/me/support-messages');
}

/**
 * I've read the replies (clears the dot on the settings gear).
 * @returns {Promise<MeSupportMessagesSeenResponse>}
 */
export function markSupportRepliesSeen() {
  return apiRequest('/me/support-messages/seen', { method: 'POST' });
}

// ── admin (客服 tab) ────────────────────────────────────────────────────────

/**
 * One row per person with messages, newest activity first.
 * @param {{ status?: 'open'|'done'|'all', q?: string }} [p]
 * @returns {Promise<AdminSupportThreadsResponse>}
 */
export function getSupportThreads({ status = 'open', q = '' } = {}) {
  const qs = new URLSearchParams({ status });
  if (q) qs.set('q', q);
  return apiRequest(`/admin/support/threads?${qs.toString()}`);
}

/**
 * The person's whole conversation (app + shop chat) plus their customer card.
 * Works for an account that never wrote (empty timeline, full card).
 * @param {string} key
 * @returns {Promise<AdminSupportThreadResponse>}
 */
export function getSupportThread(key) {
  return apiRequest(`/admin/support/threads/${encodeURIComponent(key)}`);
}

/**
 * Reply to the person's latest message, back through the door it came from.
 * Closes the thread unless keepOpen.
 * @param {string} key
 * @param {{ text: string, keepOpen?: boolean }} p
 * @returns {Promise<AdminSupportReplyResponse>}
 */
export function replySupportThread(key, { text, keepOpen = false }) {
  return apiRequest(`/admin/support/threads/${encodeURIComponent(key)}/replies`, {
    method: 'POST',
    body: JSON.stringify({ text, keepOpen: !!keepOpen }),
  });
}

/**
 * @param {string} key
 * @param {'open'|'done'} status
 * @returns {Promise<AdminSupportThreadStatusResponse>}
 */
export function setSupportThreadStatus(key, status) {
  return apiRequest(`/admin/support/threads/${encodeURIComponent(key)}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

/**
 * Find a person from a phone, name, account id, shop order number or redemption code.
 * @param {string} q
 * @returns {Promise<AdminCustomersLookupResponse>}
 */
export function lookupCustomers(q) {
  return apiRequest(`/admin/customers?q=${encodeURIComponent(q)}`);
}
